
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { getAccountOwnershipInfo } from "../../business-value-framework/accountService";
import { recordTabVisit } from "../../business-value-framework/navigationService";

type Account = {
  company_id?: string;
  region: string;
  country: string;
  company_name: string;
  parent_company?: string;
  industry: string;
  account_manager: string;
};

type KpiData = {
  executiveKPIs: string[];
  coreFinancialKPIs: string[];
  businessInitiatives: string[];
  operatingKPIs: string[];
};

// Helper function to get subordinate user IDs
const getSubordinateIds = async (supervisorId: string): Promise<string[]> => {
  try {
    const { data, error } = await supabase
      .rpc('get_user_subordinates', { supervisor_uuid: supervisorId });
    
    if (error) {
      console.error("Error fetching subordinates:", error);
      return [];
    }
    
    return data ? data.map((row: any) => row.subordinate_id) : [];
  } catch (error) {
    console.error("Error in getSubordinateIds:", error);
    return [];
  }
};

export const useCustomerKPIs = (selectedAccount: Account | null) => {
  const [isLoadingKPIs, setIsLoadingKPIs] = useState(false);
  const [rawKPIResponse, setRawKPIResponse] = useState("");
  const [hasKPIContent, setHasKPIContent] = useState(false);
  const [kpiData, setKpiData] = useState<KpiData>({
    executiveKPIs: [],
    coreFinancialKPIs: [],
    businessInitiatives: [],
    operatingKPIs: []
  });
  const [companyTickerSymbol, setCompanyTickerSymbol] = useState("");
  const [aiProvider, setAiProvider] = useState("openai");
  
  // Initialize all ownership states as false
  const [isOwner, setIsOwner] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [isSupervisor, setIsSupervisor] = useState(false);
  const [ownershipLoaded, setOwnershipLoaded] = useState(false);

  // Record tab visit when this hook is used (Customer KPIs tab is active)
  useEffect(() => {
    recordTabVisit('customer-kpis');
  }, []);

  useEffect(() => {
    if (selectedAccount?.company_id) {
      loadSavedKPIData(selectedAccount.company_id);
    } else {
      // Reset all states when no account is selected
      setIsOwner(false);
      setCanEdit(false);
      setIsSupervisor(false);
      setOwnershipLoaded(false);
    }
  }, [selectedAccount?.company_id]);

  useEffect(() => {
    if (!isLoadingKPIs && hasKPIContent && selectedAccount?.company_id && canEdit) {
      const allCategoriesHaveContent = 
        kpiData.executiveKPIs.length > 0 && 
        kpiData.businessInitiatives.length > 0 &&
        kpiData.coreFinancialKPIs.length > 0 &&
        kpiData.operatingKPIs.length > 0;
      
      if (allCategoriesHaveContent) {
        saveKPIData();
        console.log("Auto-saving KPIs after loading completed");
      }
    }
  }, [isLoadingKPIs, hasKPIContent, kpiData, canEdit]);

  const loadSavedKPIData = async (companyId: string) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) {
        console.error("User not authenticated");
        return;
      }

      // Get account ownership information
      const ownershipInfo = await getAccountOwnershipInfo(companyId);
      
      console.log("Account ownership info:", ownershipInfo);

      // Determine final ownership states
      const userIsOwner = ownershipInfo.isOwner;
      const userCanEdit = ownershipInfo.canEdit;
      
      // Only set as supervisor if user is NOT the owner AND can view the account
      // This means they have supervisor privileges through subordinate relationship
      let userIsSupervisor = false;
      if (!userIsOwner && ownershipInfo.canView) {
        // Double-check by verifying the subordinate relationship
        const subordinates = await getSubordinateIds(userData.user.id);
        userIsSupervisor = subordinates.includes(ownershipInfo.accountOwnerUserId || '');
      }
      
      console.log("User ownership status:", { userIsOwner, userCanEdit, userIsSupervisor });
      
      // Set all states atomically to prevent race conditions
      setIsOwner(userIsOwner);
      setCanEdit(userCanEdit);
      setIsSupervisor(userIsSupervisor);
      setOwnershipLoaded(true);

      // Determine which user's data to load
      const targetUserId = userIsOwner 
        ? userData.user.id 
        : ownershipInfo.accountOwnerUserId;

      if (!targetUserId) {
        console.error("No target user ID found");
        return;
      }

      console.log("Loading KPI data for user:", targetUserId);

      // Load KPI data for the target user (account owner)
      let { data, error } = await supabase
        .from('customer_kpis')
        .select('*')
        .eq('company_id', companyId)
        .eq('user_id', targetUserId)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error("Error fetching KPI data:", error);
        return;
      }

      if (data) {
        setCompanyTickerSymbol(data.company_ticker || "");
        
        const execKpis = Array.isArray(data.exec_kpis) 
          ? data.exec_kpis.map(item => String(item)) 
          : [];
          
        const businessInitiatives = Array.isArray(data.business_initiatives)
          ? data.business_initiatives.map(item => String(item))
          : [];
          
        const coreFinancialKpis = Array.isArray(data.core_financial_kpis)
          ? data.core_financial_kpis.map(item => String(item))
          : [];
          
        const operatingKpis = Array.isArray(data.operating_kpis)
          ? data.operating_kpis.map(item => String(item))
          : [];
        
        setKpiData({
          executiveKPIs: execKpis,
          businessInitiatives: businessInitiatives,
          coreFinancialKPIs: coreFinancialKpis,
          operatingKPIs: operatingKpis
        });
        
        setHasKPIContent(true);
        setRawKPIResponse(`Loaded saved KPI data for ${data.company_ticker}`);
        console.log("Loaded KPI data:", execKpis, businessInitiatives, coreFinancialKpis, operatingKpis);
      } else {
        console.log("No KPI data found for company ID:", companyId);
        setKpiData({
          executiveKPIs: [],
          businessInitiatives: [],
          coreFinancialKPIs: [],
          operatingKPIs: []
        });
        setHasKPIContent(false);
      }
    } catch (error) {
      console.error("Error loading saved KPI data:", error);
      // Reset all states on error
      setIsOwner(false);
      setCanEdit(false);
      setIsSupervisor(false);
      setOwnershipLoaded(false);
    }
  };

  const saveKPIData = async () => {
    if (!selectedAccount?.company_id) {
      console.error("No account selected");
      return;
    }

    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) {
        console.error("User not authenticated");
        return;
      }

      // Get account ownership information
      const ownershipInfo = await getAccountOwnershipInfo(selectedAccount.company_id);
      
      // Only allow account owners to save KPI data
      if (!ownershipInfo.canEdit) {
        toast.error("Only account owners can save KPI data");
        return;
      }

      // Only the account owner can create/update records in their name
      if (!ownershipInfo.isOwner) {
        toast.error("Only account owners can modify KPI data");
        return;
      }

      const { data: existingData } = await supabase
        .from('customer_kpis')
        .select('id')
        .eq('company_id', selectedAccount.company_id)
        .eq('user_id', userData.user.id)
        .maybeSingle();

      const kpiRecord = {
        user_id: userData.user.id,
        company_id: selectedAccount.company_id,
        company_ticker: companyTickerSymbol,
        exec_kpis: kpiData.executiveKPIs,
        business_initiatives: kpiData.businessInitiatives,
        core_financial_kpis: kpiData.coreFinancialKPIs,
        operating_kpis: kpiData.operatingKPIs
      };

      console.log("Saving KPI data:", kpiRecord);

      let result;
      if (existingData) {
        result = await supabase
          .from('customer_kpis')
          .update(kpiRecord)
          .eq('id', existingData.id);
      } else {
        result = await supabase
          .from('customer_kpis')
          .insert([kpiRecord]);
      }

      if (result.error) {
        console.error("Error saving KPI data:", result.error);
        toast.error("Failed to save KPI data");
      } else {
        console.log("KPI data saved successfully");
        toast.success("KPI data saved successfully");
      }
    } catch (error) {
      console.error("Error saving KPI data:", error);
      toast.error("Failed to save KPI data");
    }
  };

  return {
    isLoadingKPIs,
    setIsLoadingKPIs,
    rawKPIResponse,
    setRawKPIResponse,
    hasKPIContent,
    setHasKPIContent,
    kpiData,
    setKpiData,
    companyTickerSymbol,
    setCompanyTickerSymbol,
    aiProvider,
    setAiProvider,
    saveKPIData,
    isOwner,
    canEdit,
    isSupervisor,
    ownershipLoaded
  };
};
